import { Formatter, type BaseCurrency } from '@coin-tracker/number-formatter';
import { Decimal } from 'decimal.js';
import { calculateTax } from '../Rebrand/TradeCalculator/TaxMath';
import type { CoinInfoType, CurrencyInfoType } from './types';

export function hasOnlyNumericalCharacters(value: string) {
  return /^[\d,.]*$/.test(value);
}

export function convertToBaseCurrencyValue(
  value: string | number,
  base?: BaseCurrency,
) {
  return Formatter.formatFiat(
    typeof value === 'number' ? value : parseFloat(value),
    base,
    {
      showSymbol: false,
      isAbbreviated: false,
      isShortened: false,
    },
  );
}

export function convertAllToBaseCurrencyValue(
  values: Record<string, string | number>,
  base?: BaseCurrency,
) {
  const convertedValues: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    convertedValues[key] = convertToBaseCurrencyValue(value, base);
  }
  return convertedValues;
}

function numberIsUSFormat(value: string) {
  // regex to check if the number is a valid US formatted number and not written in the EU style --eg: 1,000,000.00 => true; 1.000.000,00 => false
  return /^(\d{1,3})(,\d{3})*(\.\d+)?$/.test(value);
}
function convertEUFormatToUSFormat(value: string) {
  // should return a EU style value to a US style value --eg: 1.000.000,00 => 1,000,000.00
  return value.replace(/,/g, '|').replace(/\./g, ',').replace(/\|/g, '.');
}

function getDecimal(value: string) {
  const convertedValue = numberIsUSFormat(value)
    ? value
    : convertEUFormatToUSFormat(value);
  // make a decimal with only the numbers and decimal point from the value
  return new Decimal(Number(convertedValue.replace(/[^0-9\\.-]+/g, '')));
}

function getDefaultedDecimal(value: string) {
  return value.length > 0 ? getDecimal(value) : new Decimal(0);
}

function decimalToBaseCurrencyFormatted(
  value: Decimal,
  baseCurrency?: CurrencyInfoType,
) {
  return Formatter.formatFiat(parseFloat(value.toString()), baseCurrency);
}

export function findCurrencyById(
  currencies: CurrencyInfoType[],
  value?: string,
) {
  return currencies?.find((c) => c.id === value) as CurrencyInfoType;
}

export function findCoinBySlug(coins: CoinInfoType[], value?: string) {
  return coins?.find((c) => c.slug === value) as CoinInfoType;
}

export function calculateProfitLoss({
  selectedCurrency,
  buyPrice,
  sellPrice,
  investment,
  investmentFee,
  exitFee,
  state = '',
  totalIncome = '0',
  filingStatus = 'single',
  holdingPeriod = 'short',
  tradeMode = 'single',
  totalCryptoGains = '0',
}: {
  selectedCurrency?: CurrencyInfoType | undefined | null;
  buyPrice?: string;
  sellPrice?: string;
  investment?: string;
  investmentFee?: string;
  exitFee?: string;
  state?: string;
  totalIncome?: string;
  filingStatus?: 'single' | 'married';
  holdingPeriod?: 'short' | 'long';
  tradeMode?: 'single' | 'multiple';
  totalCryptoGains?: string;
}) {
  let profitLoss: Decimal;
  let totalInvestmentAmount: Decimal;
  let totalExitAmount: Decimal;

  if (tradeMode === 'multiple') {
    const decimalTotalCryptoGains = getDefaultedDecimal(
      totalCryptoGains ?? '0',
    );
    profitLoss = decimalTotalCryptoGains;
    totalInvestmentAmount = new Decimal(0);
    totalExitAmount = decimalTotalCryptoGains;
  } else {
    const decimalBuyPrice = getDefaultedDecimal(buyPrice ?? '0');
    const decimalSellPrice = getDefaultedDecimal(sellPrice ?? '0');
    const decimalInvestment = getDefaultedDecimal(investment ?? '0');
    const decimalInvestmentFee = getDefaultedDecimal(investmentFee ?? '0');
    const decimalExitFee = getDefaultedDecimal(exitFee ?? '0');

    totalInvestmentAmount = decimalInvestment.minus(decimalInvestmentFee);
    const investmentAsCoin = totalInvestmentAmount.dividedBy(decimalBuyPrice);
    const priceDelta = decimalSellPrice.minus(decimalBuyPrice);

    profitLoss = totalInvestmentAmount.gt(0)
      ? investmentAsCoin.times(priceDelta)
      : new Decimal(0);

    totalExitAmount = totalInvestmentAmount
      .plus(profitLoss)
      .minus(decimalExitFee);
  }

  const profitLossPercentage = !profitLoss.eq(0)
    ? totalInvestmentAmount.eq(0)
      ? new Decimal(0)
      : profitLoss.dividedBy(totalInvestmentAmount).times(100)
    : new Decimal(0);

  const profitLossDelta = profitLoss.toString();

  const taxResult = calculateTax({
    income: parseFloat(totalIncome) || 0,
    shortTermCapitalGains:
      holdingPeriod === 'short'
        ? Math.max(parseFloat(profitLossDelta) || 0, 0)
        : 0,
    longTermCapitalGains:
      holdingPeriod === 'long'
        ? Math.max(parseFloat(profitLossDelta) || 0, 0)
        : 0,
    filingStatus,
    state,
  });

  const taxPercentage =
    !profitLoss.eq(0) && profitLoss.gt(0)
      ? new Decimal(taxResult.capitalGainsTax)
          .dividedBy(parseFloat(profitLossDelta))
          .times(100)
          .round()
          .toString()
      : '0';

  return {
    profitLoss: decimalToBaseCurrencyFormatted(
      profitLoss,
      selectedCurrency ?? undefined,
    ),
    profitLossDelta,
    profitLossPercentage: parseFloat(
      profitLossPercentage.toFixed(2),
    ).toLocaleString('en-us', { minimumFractionDigits: 2 }),
    totalExitAmount: decimalToBaseCurrencyFormatted(
      totalExitAmount,
      selectedCurrency ?? undefined,
    ),
    totalInvestmentAmount: decimalToBaseCurrencyFormatted(
      totalInvestmentAmount,
      selectedCurrency ?? undefined,
    ),
    taxAmount: decimalToBaseCurrencyFormatted(
      new Decimal(taxResult.capitalGainsTax),
      selectedCurrency ?? undefined,
    ),
    afterTaxProfit: decimalToBaseCurrencyFormatted(
      new Decimal(
        parseFloat(profitLossDelta) -
          (profitLoss.gt(0) ? taxResult.capitalGainsTax : 0),
      ),
      selectedCurrency ?? undefined,
    ),
    taxPercentage,
  };
}
